import { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore, startOfToday } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const AvailabilityCalendar = ({ bookedDates = [], minStayDays = 1, onDateSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = startOfToday();

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get the day of week the month starts on (0 = Sunday, 6 = Saturday)
  const startDayOfWeek = monthStart.getDay();

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const isDateBooked = (date) => {
    return bookedDates.some((bookedDate) => {
      const booked = new Date(bookedDate);
      return format(date, 'yyyy-MM-dd') === format(booked, 'yyyy-MM-dd');
    });
  };

  const isDatePast = (date) => {
    return isBefore(date, today);
  };

  const handleDateClick = (date) => {
    if (!isDatePast(date) && !isDateBooked(date) && onDateSelect) {
      onDateSelect(date);
    }
  };

  return (
    <div className="bg-white p-6 rounded-sm border border-stone-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={previousMonth}
          className="p-2 hover:bg-stone-100 rounded-sm transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-semibold">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-stone-100 rounded-sm transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="text-center text-xs font-medium text-stone-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before month starts */}
        {Array.from({ length: startDayOfWeek }).map((_, index) => (
          <div key={`empty-${index}`} className="aspect-square" />
        ))}

        {/* Actual days */}
        {daysInMonth.map((date) => {
          const isPast = isDatePast(date);
          const isBooked = isDateBooked(date);
          const isTodayDate = isToday(date);
          const isAvailable = !isPast && !isBooked;

          return (
            <button
              key={date.toISOString()}
              onClick={() => handleDateClick(date)}
              disabled={isPast || isBooked}
              className={`
                aspect-square flex items-center justify-center text-sm rounded-sm transition-colors relative
                ${
                  isPast
                    ? 'text-stone-300 cursor-not-allowed bg-stone-50'
                    : isBooked
                    ? 'bg-red-50 text-red-600 cursor-not-allowed font-medium'
                    : isAvailable
                    ? 'hover:bg-primary hover:text-white cursor-pointer font-medium'
                    : ''
                }
                ${
                  isTodayDate && !isBooked
                    ? 'ring-2 ring-primary ring-inset'
                    : ''
                }
              `}
              data-testid={`calendar-day-${format(date, 'yyyy-MM-dd')}`}
            >
              {/* Strikethrough for booked dates */}
              {isBooked && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-0.5 bg-red-500 rotate-[-25deg]" />
                </div>
              )}
              <span className={isBooked ? 'relative z-10' : ''}>
                {format(date, 'd')}
              </span>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4 text-xs">
        <div className="flex items-center">
          <div className="w-6 h-6 bg-white border border-stone-200 rounded-sm mr-2 flex items-center justify-center">
            <span className="font-medium">1</span>
          </div>
          <span className="text-stone-600">Available</span>
        </div>
        <div className="flex items-center">
          <div className="w-6 h-6 bg-red-50 rounded-sm mr-2 flex items-center justify-center relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-0.5 bg-red-500 rotate-[-25deg]" />
            </div>
            <span className="text-red-600 font-medium text-xs relative z-10">1</span>
          </div>
          <span className="text-stone-600">Unavailable</span>
        </div>
        <div className="flex items-center">
          <div className="w-6 h-6 bg-stone-50 rounded-sm mr-2 flex items-center justify-center">
            <span className="text-stone-300 text-xs">1</span>
          </div>
          <span className="text-stone-600">Past</span>
        </div>
      </div>

      {minStayDays > 1 && (
        <p className="text-xs text-stone-500 mt-4 text-center">
          Minimum stay: {minStayDays} nights
        </p>
      )}
    </div>
  );
};

export default AvailabilityCalendar;
